import {TemplateNode} from "#template/template-node/template-node"
import {ExportNode} from "#template/fundamental/export/export-node"
import {isMeshLikeNode} from "#template/nodes/object/mesh/marker"
import type {MeshNodeParameters} from "#template/nodes/object/mesh/types"
import type {NodeEvaluator} from "#template/node-evaluator"
import type {EvaluatedTemplateInput} from "#template/fundamental/evaluated-input"
import type {BuilderInlet, BuilderOutlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {TemplateNodePart} from "#template/interactions"
import {WaitReady} from "#template/runtime-graph/nodes/wait-ready"
import {NotReady} from "#template/runtime-graph/slots"

export const collectAffectedParts = (
    affectedNodes: Set<TemplateNode>,
    activeNodeSet: Set<TemplateNode> | undefined,
): {parts: TemplateNodePart[]; affectedOutputIds: string[]} => {
    const result: TemplateNodePart[] = []
    const affectedOutputIds: string[] = []
    for (const affectedNode of affectedNodes) {
        if (activeNodeSet && !activeNodeSet.has(affectedNode)) continue

        if (affectedNode instanceof ExportNode) {
            affectedOutputIds.push((affectedNode.parameters as {id: string}).id)
        }

        if (isMeshLikeNode(affectedNode)) {
            const {materialAssignments, ...otherParams} = affectedNode.parameters as MeshNodeParameters
            const reachedDirectly = Object.values(otherParams).some((v) => v instanceof TemplateNode && affectedNodes.has(v))
            if (reachedDirectly) {
                result.push({templateNode: affectedNode, part: "root"})
            }
            if (affectedNodes.has(materialAssignments)) {
                for (const slot in materialAssignments.parameters) {
                    const assignment = materialAssignments.parameters[slot]
                    if (assignment && affectedNodes.has(assignment)) {
                        result.push({templateNode: affectedNode, part: `group${Number(slot)}`})
                    }
                }
            }
        } else {
            result.push({templateNode: affectedNode, part: "root"})
        }
    }

    return {parts: result, affectedOutputIds}
}

// Includes `rootNode` and all its transitive parents, but does not walk through parents the caller marks
// as a traversal boundary (e.g. output nodes), then collects the affected parts. The boundary check is
// injected so this stays agnostic of the concrete fundamental node classes.
export const getAffectedTemplateNodeParts = (
    rootNode: TemplateNode,
    activeNodeSet: Set<TemplateNode> | undefined,
    isTraversalBoundary: (node: TemplateNode) => boolean,
): {parts: TemplateNodePart[]; affectedOutputIds: string[]} => {
    const affectedNodes = new Set<TemplateNode>()

    const visit = (node: TemplateNode) => {
        if (affectedNodes.has(node)) return
        affectedNodes.add(node)
        node.parents.forEach((parent) => {
            if (parent instanceof TemplateNode && !isTraversalBoundary(parent)) {
                visit(parent)
            }
        })
    }

    visit(rootNode)

    return collectAffectedParts(affectedNodes, activeNodeSet)
}

const resolveTemplateInputEntry = <R>(entry: EvaluatedTemplateInput, consumerType: string, externalId: string) => {
    if (entry.ancestry.includes(consumerType)) {
        return {value: entry.value as R, origin: entry.origin}
    } else throw Error(`Type mismatch for input ${externalId}: expected ${consumerType}, got ${entry.type}`)
}

export const resolveTemplateInput = <R>(
    scope: GraphBuilderScope,
    evaluator: NodeEvaluator,
    externalId: string,
    consumerType: string,
): BuilderInlet<{value: R; origin: TemplateNode | undefined}> | undefined => {
    const {ambientInputs} = evaluator
    const entry = ambientInputs[externalId]

    if (entry === undefined) return undefined

    if (entry !== NotReady) {
        return resolveTemplateInputEntry<R>(entry, consumerType, externalId)
    }

    const waitReady = scope.node(WaitReady<EvaluatedTemplateInput>, {
        input: entry,
    })

    return scope.pureLambda(
        scope.tuple(waitReady.output as BuilderOutlet<EvaluatedTemplateInput>, consumerType, externalId),
        ([entry, consumerType, externalId]) => resolveTemplateInputEntry<R>(entry, consumerType, externalId),
        "templateInput",
    )
}
